<template>
    <button
        class="iu-button"
        @click="handleClick"
        :disabled="buttonDisabled || loading"
        :autofocus="autofocus"
        :type="nativeType"
        :class="[
            type ? 'iu-button--' + type : '',
            buttonSize ? 'iu-button--' + buttonSize : '',
            {
                'is-disabled': buttonDisabled,
                'is-loading': loading,
                'is-plain': plain,
                'is-round': round,
                'is-circle': circle,
            },
        ]"
    >
        <i class="iu-icon-loading" v-if="loading"></i>
        <i :class="icon" v-if="icon && !loading"></i>
        <span v-if="$slots.default">
            <slot></slot>
        </span>
    </button>
</template>

<script>
export default {
    name: 'IuButton',
    inject: {
        elForm: {
            default: '',
        },
        elFormItem: {
            default: '',
        },
    },
    props: {
        type: {
            type: String,
            default: 'default',
        },
        size: String,
        icon: {
            type: String,
            default: '',
        },
        nativeType: {
            type: String,
            default: 'button',
        },
        loading: Boolean,
        disabled: Boolean,
        plain: Boolean,
        autofocus: Boolean,
        round: Boolean,
        circle: Boolean,
    },
    computed: {
        _elFormItemSize() {
            return (this.elFormItem || {}).elFormItemSize;
        },
        buttonSize() {
            return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
        },
        buttonDisabled() {
            return this.disabled || (this.elForm || {}).disabled;
        },
    },
    methods: {
        handleClick(evt) {
            this.$emit('click', evt);
        },
    },
};
</script>

<style scope>
.iu-button-group > .iu-button.is-active,
.iu-button-group > .iu-button.is-disabled,
.iu-button-group > .iu-button:active,
.iu-button-group > .iu-button:focus,
.iu-button-group > .iu-button:hover {
    z-index: 1;
}
.iu-button {
    display: inline-block;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    background: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;
    -webkit-appearance: none;
    text-align: center;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    outline: 0;
    margin: 0;
    -webkit-transition: 0.1s;
    transition: 0.1s;
    font-weight: 500;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 4px;
}
.iu-button + .iu-button {
    margin-left: 10px;
}
.iu-button:focus,
.iu-button:hover {
    color: #409eff;
    border-color: #c6e2ff;
    background-color: #ecf5ff;
}
.iu-button:active {
    color: #3a8ee6;
    border-color: #3a8ee6;
    outline: 0;
}
.iu-button::-moz-focus-inner {
    border: 0;
}
.iu-button [class*='iu-icon-'] + span {
    margin-left: 5px;
}
.iu-button.is-plain:focus,
.iu-button.is-plain:hover {
    background: #fff;
    border-color: #409eff;
    color: #409eff;
}
.iu-button.is-active,
.iu-button.is-plain:active {
    color: #3a8ee6;
    border-color: #3a8ee6;
}
.iu-button.is-plain:active {
    background: #fff;
    outline: 0;
}
.iu-button.is-disabled,
.iu-button.is-disabled:focus,
.iu-button.is-disabled:hover {
    color: #c0c4cc;
    cursor: not-allowed;
    background-image: none;
    background-color: #fff;
    border-color: #ebeef5;
}
.iu-button.is-disabled.iu-button--text {
    background-color: transparent;
}
.iu-button.is-disabled.is-plain,
.iu-button.is-disabled.is-plain:focus,
.iu-button.is-disabled.is-plain:hover {
    background-color: #fff;
    border-color: #ebeef5;
    color: #c0c4cc;
}
.iu-button.is-loading {
    position: relative;
    pointer-events: none;
}
.iu-button.is-loading:before {
    pointer-events: none;
    content: '';
    position: absolute;
    left: -1px;
    top: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: inherit;
    background-color: rgba(255, 255, 255, 0.35);
}
.iu-button.is-round {
    border-radius: 20px;
    padding: 12px 23px;
}
.iu-button.is-circle {
    border-radius: 50%;
    padding: 12px;
}
.iu-button--primary {
    color: #fff;
    background-color: #409eff;
    border-color: #409eff;
}
.iu-button--primary:focus,
.iu-button--primary:hover {
    background: #66b1ff;
    border-color: #66b1ff;
    color: #fff;
}
.iu-button--primary.is-active,
.iu-button--primary:active {
    background: #3a8ee6;
    border-color: #3a8ee6;
    color: #fff;
}
.iu-button--primary:active {
    outline: 0;
}
.iu-button--primary.is-disabled,
.iu-button--primary.is-disabled:active,
.iu-button--primary.is-disabled:focus,
.iu-button--primary.is-disabled:hover {
    color: #fff;
    background-color: #a0cfff;
    border-color: #a0cfff;
}
.iu-button--primary.is-plain {
    color: #409eff;
    background: #ecf5ff;
    border-color: #b3d8ff;
}
.iu-button--primary.is-plain:focus,
.iu-button--primary.is-plain:hover {
    background: #409eff;
    border-color: #409eff;
    color: #fff;
}
.iu-button--primary.is-plain:active {
    background: #3a8ee6;
    border-color: #3a8ee6;
    color: #fff;
    outline: 0;
}
.iu-button--primary.is-plain.is-disabled,
.iu-button--primary.is-plain.is-disabled:active,
.iu-button--primary.is-plain.is-disabled:focus,
.iu-button--primary.is-plain.is-disabled:hover {
    color: #8cc5ff;
    background-color: #ecf5ff;
    border-color: #d9ecff;
}
.iu-button--success {
    color: #fff;
    background-color: #67c23a;
    border-color: #67c23a;
}
.iu-button--success:focus,
.iu-button--success:hover {
    background: #85ce61;
    border-color: #85ce61;
    color: #fff;
}
.iu-button--success.is-active,
.iu-button--success:active {
    background: #5daf34;
    border-color: #5daf34;
    color: #fff;
}
.iu-button--success:active {
    outline: 0;
}
.iu-button--success.is-disabled,
.iu-button--success.is-disabled:active,
.iu-button--success.is-disabled:focus,
.iu-button--success.is-disabled:hover {
    color: #fff;
    background-color: #b3e19d;
    border-color: #b3e19d;
}
.iu-button--success.is-plain {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
}
.iu-button--success.is-plain:focus,
.iu-button--success.is-plain:hover {
    background: #67c23a;
    border-color: #67c23a;
    color: #fff;
}
.iu-button--success.is-plain:active {
    background: #5daf34;
    border-color: #5daf34;
    color: #fff;
    outline: 0;
}
.iu-button--success.is-plain.is-disabled,
.iu-button--success.is-plain.is-disabled:active,
.iu-button--success.is-plain.is-disabled:focus,
.iu-button--success.is-plain.is-disabled:hover {
    color: #a4da89;
    background-color: #f0f9eb;
    border-color: #e1f3d8;
}
.iu-button--warning {
    color: #fff;
    background-color: #e6a23c;
    border-color: #e6a23c;
}
.iu-button--warning:focus,
.iu-button--warning:hover {
    background: #ebb563;
    border-color: #ebb563;
    color: #fff;
}
.iu-button--warning.is-active,
.iu-button--warning:active {
    background: #cf9236;
    border-color: #cf9236;
    color: #fff;
}
.iu-button--warning:active {
    outline: 0;
}
.iu-button--warning.is-disabled,
.iu-button--warning.is-disabled:active,
.iu-button--warning.is-disabled:focus,
.iu-button--warning.is-disabled:hover {
    color: #fff;
    background-color: #f3d19e;
    border-color: #f3d19e;
}
.iu-button--warning.is-plain {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
}
.iu-button--warning.is-plain:focus,
.iu-button--warning.is-plain:hover {
    background: #e6a23c;
    border-color: #e6a23c;
    color: #fff;
}
.iu-button--warning.is-plain:active {
    background: #cf9236;
    border-color: #cf9236;
    color: #fff;
    outline: 0;
}
.iu-button--warning.is-plain.is-disabled,
.iu-button--warning.is-plain.is-disabled:active,
.iu-button--warning.is-plain.is-disabled:focus,
.iu-button--warning.is-plain.is-disabled:hover {
    color: #f0c78a;
    background-color: #fdf6ec;
    border-color: #faecd8;
}
.iu-button--danger {
    color: #fff;
    background-color: #f56c6c;
    border-color: #f56c6c;
}
.iu-button--danger:focus,
.iu-button--danger:hover {
    background: #f78989;
    border-color: #f78989;
    color: #fff;
}
.iu-button--danger.is-active,
.iu-button--danger:active {
    background: #dd6161;
    border-color: #dd6161;
    color: #fff;
}
.iu-button--danger:active {
    outline: 0;
}
.iu-button--danger.is-disabled,
.iu-button--danger.is-disabled:active,
.iu-button--danger.is-disabled:focus,
.iu-button--danger.is-disabled:hover {
    color: #fff;
    background-color: #fab6b6;
    border-color: #fab6b6;
}
.iu-button--danger.is-plain {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
}
.iu-button--danger.is-plain:focus,
.iu-button--danger.is-plain:hover {
    background: #f56c6c;
    border-color: #f56c6c;
    color: #fff;
}
.iu-button--danger.is-plain:active {
    background: #dd6161;
    border-color: #dd6161;
    color: #fff;
    outline: 0;
}
.iu-button--danger.is-plain.is-disabled,
.iu-button--danger.is-plain.is-disabled:active,
.iu-button--danger.is-plain.is-disabled:focus,
.iu-button--danger.is-plain.is-disabled:hover {
    color: #f9a7a7;
    background-color: #fef0f0;
    border-color: #fde2e2;
}
.iu-button--info {
    color: #fff;
    background-color: #909399;
    border-color: #909399;
}
.iu-button--info:focus,
.iu-button--info:hover {
    background: #a6a9ad;
    border-color: #a6a9ad;
    color: #fff;
}
.iu-button--info.is-active,
.iu-button--info:active {
    background: #82848a;
    border-color: #82848a;
    color: #fff;
}
.iu-button--info:active {
    outline: 0;
}
.iu-button--info.is-disabled,
.iu-button--info.is-disabled:active,
.iu-button--info.is-disabled:focus,
.iu-button--info.is-disabled:hover {
    color: #fff;
    background-color: #c8c9cc;
    border-color: #c8c9cc;
}
.iu-button--info.is-plain {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
}
.iu-button--info.is-plain:focus,
.iu-button--info.is-plain:hover {
    background: #909399;
    border-color: #909399;
    color: #fff;
}
.iu-button--info.is-plain:active {
    background: #82848a;
    border-color: #82848a;
    color: #fff;
    outline: 0;
}
.iu-button--info.is-plain.is-disabled,
.iu-button--info.is-plain.is-disabled:active,
.iu-button--info.is-plain.is-disabled:focus,
.iu-button--info.is-plain.is-disabled:hover {
    color: #bcbec2;
    background-color: #f4f4f5;
    border-color: #e9e9eb;
}
.iu-button--text,
.iu-button--text.is-disabled,
.iu-button--text.is-disabled:focus,
.iu-button--text.is-disabled:hover,
.iu-button--text:active {
    border-color: transparent;
}
.iu-button--medium {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 4px;
}
.iu-button--mini,
.iu-button--small {
    font-size: 12px;
    border-radius: 3px;
}
.iu-button--medium.is-round {
    padding: 10px 20px;
}
.iu-button--medium.is-circle {
    padding: 10px;
}
.iu-button--small,
.iu-button--small.is-round {
    padding: 9px 15px;
}
.iu-button--small.is-circle {
    padding: 9px;
}
.iu-button--mini,
.iu-button--mini.is-round {
    padding: 7px 15px;
}
.iu-button--mini.is-circle {
    padding: 7px;
}
.iu-button--text {
    color: #409eff;
    background: 0 0;
    padding-left: 0;
    padding-right: 0;
}
.iu-button--text:focus,
.iu-button--text:hover {
    color: #66b1ff;
    border-color: transparent;
    background-color: transparent;
}
.iu-button--text:active {
    color: #3a8ee6;
    background-color: transparent;
}
.iu-button-group {
    display: inline-block;
    vertical-align: middle;
}
.iu-button-group::after,
.iu-button-group::before {
    display: table;
    content: '';
}
.iu-button-group::after {
    clear: both;
}
.iu-button-group > .iu-button {
    float: left;
    position: relative;
}
.iu-button-group > .iu-button + .iu-button {
    margin-left: 0;
}
.iu-button-group > .iu-button:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.iu-button-group > .iu-button:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.iu-button-group > .iu-button:first-child:last-child {
    border-radius: 4px;
}
.iu-button-group > .iu-button:first-child:last-child.is-round {
    border-radius: 20px;
}
.iu-button-group > .iu-button:first-child:last-child.is-circle {
    border-radius: 50%;
}
.iu-button-group > .iu-button:not(:first-child):not(:last-child) {
    border-radius: 0;
}
.iu-button-group > .iu-button:not(:last-child) {
    margin-right: -1px;
}
.iu-button-group > .iu-dropdown > .iu-button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--primary:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--success:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--warning:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--danger:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:first-child {
    border-right-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:last-child {
    border-left-color: rgba(255, 255, 255, 0.5);
}
.iu-button-group .iu-button--info:not(:first-child):not(:last-child) {
    border-left-color: rgba(255, 255, 255, 0.5);
    border-right-color: rgba(255, 255, 255, 0.5);
}
</style>
